import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Activity, Building2, MapPin, QrCode, AlertCircle, CheckCircle2, Clock, Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";

const PERIODS = [
  { v: "today", l: "Hoy" },
  { v: "7d", l: "Últimos 7 días" },
  { v: "30d", l: "Últimos 30 días" },
  { v: "month", l: "Este mes" },
  { v: "year", l: "Último año" },
  { v: "ytd", l: "Este año" },
  { v: "custom", l: "Personalizado" },
];

const STATUS_COLORS = {
  limpio: "#10B981",
  requiere_atencion: "#F59E0B",
  insumos_faltantes: "#F59E0B",
  problema: "#EF4444",
};

const STATUS_LABELS = {
  limpio: "Limpio",
  requiere_atencion: "Requiere atención",
  insumos_faltantes: "Insumos faltantes",
  problema: "Problema",
};

const StatCard = ({ icon: Icon, label, value, color = "#00AEEF", testid }) => (
  <div className="lx-stat" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="lx-overline" style={{ color: "#64748B" }}>{label}</div>
        <div className="font-heading font-bold text-3xl mt-2 tracking-tight">{value}</div>
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: color + "22", color }}>
        <Icon size={20} strokeWidth={1.8} />
      </div>
    </div>
  </div>
);

const statusBadge = (s) => {
  const map = {
    limpio: "lx-badge-success",
    requiere_atencion: "lx-badge-warning",
    insumos_faltantes: "lx-badge-warning",
    problema: "lx-badge-error",
  };
  return <span className={`lx-badge ${map[s] || "lx-badge-muted"}`}>{STATUS_LABELS[s] || s}</span>;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const fmtDay = (s) => {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
};

function ComplianceColor(p) {
  if (p === null || p === undefined) return "#94A3B8";
  if (p >= 85) return "#10B981";
  if (p >= 60) return "#F59E0B";
  return "#EF4444";
}

function ComplianceGauge({ value, label, size = 160 }) {
  const v = value === null || value === undefined ? 0 : Math.min(value, 100);
  const color = ComplianceColor(value);
  const r = (size - 16) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (v / 100) * c;
  return (
    <div className="flex flex-col items-center justify-center" data-testid="compliance-gauge">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} stroke="#E2E8F0" strokeWidth="10" fill="none"/>
          <circle cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth="10" fill="none"
                  strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.3s ease" }}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-heading font-bold text-4xl tracking-tight" style={{ color }}>
            {value === null ? "—" : `${Math.round(value)}%`}
          </div>
          <div className="text-xs text-lx-muted">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isClient = user?.role === "cliente";
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [stats, setStats] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [myClient, setMyClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [complianceLoading, setComplianceLoading] = useState(false);
  // Compliance chart controls
  const [complianceScope, setComplianceScope] = useState(isClient ? "location" : "client");
  const [complianceClientFilter, setComplianceClientFilter] = useState("");
  const [allClients, setAllClients] = useState([]);

  // Initial load + when period changes: load everything
  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    (async () => {
      setLoading(true);
      try {
        const params = { period };
        if (period === "custom") { params.start = customStart; params.end = customEnd; }
        const complianceParams = { ...params, scope: complianceScope };
        if (complianceClientFilter && !isClient) complianceParams.client_id = complianceClientFilter;
        const reqs = [
          api.get("/dashboard/stats"),
          api.get("/dashboard/compliance", { params: complianceParams }),
          api.get("/dashboard/trends", { params: { period: "30d" } }),
          api.get("/dashboard/distribution", { params }),
          api.get("/clients"),
        ];
        const results = await Promise.all(reqs);
        setStats(results[0].data);
        setCompliance(results[1].data);
        setTrends(results[2].data);
        setDistribution(results[3].data);
        if (results[4]?.data?.length) {
          if (isClient) setMyClient(results[4].data[0]);
          else setAllClients(results[4].data);
        }
      } finally { setLoading(false); }
    })();
  }, [isClient, period, customStart, customEnd]);

  // Refetch only compliance when scope or client filter changes (no full-page reload)
  useEffect(() => {
    if (loading) return; // skip during initial load (already fetched there)
    if (period === "custom" && (!customStart || !customEnd)) return;
    (async () => {
      setComplianceLoading(true);
      try {
        const params = { period, scope: complianceScope };
        if (period === "custom") { params.start = customStart; params.end = customEnd; }
        if (complianceClientFilter && !isClient) params.client_id = complianceClientFilter;
        const { data } = await api.get("/dashboard/compliance", { params });
        setCompliance(data);
      } finally { setComplianceLoading(false); }
    })();
  }, [complianceScope, complianceClientFilter]);

  const trendData = useMemo(() => {
    if (!trends?.series) return [];
    return trends.series.map((s) => ({ date: fmtDay(s.date), checks: s.checks }));
  }, [trends]);

  const distData = useMemo(() => {
    if (!distribution?.by_status) return [];
    return Object.entries(distribution.by_status).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, key: k }));
  }, [distribution]);

  const topLocations = useMemo(() => {
    if (!compliance?.items) return [];
    return [...compliance.items]
      .filter(i => i.expected > 0)
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .slice(0, 8)
      .map(i => ({ name: i.name, percentage: i.percentage || 0, actual: i.actual, expected: i.expected }));
  }, [compliance]);

  if (loading || !stats) return <div className="text-lx-muted">Cargando dashboard…</div>;

  const periodLabel = PERIODS.find(p => p.v === period)?.l || "Hoy";

  return (
    <div className="space-y-6">
      {isClient ? (
        <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #00AEEF, #008CBE)" }} data-testid="client-welcome-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 font-bold">Portal de cliente</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight mt-1" data-testid="client-company-name">
                {myClient?.name || "Bienvenido"}
              </h1>
              {myClient?.company_name && (
                <p className="text-white/85 text-sm mt-1">{myClient.company_name}</p>
              )}
              <p className="text-white/85 mt-3 max-w-md">
                Consulta el estado de limpieza en tus sucursales en tiempo real — {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}
              </p>
            </div>
            <div className="hidden sm:flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15">
              <Sparkles size={32} strokeWidth={1.8}/>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="lx-overline">Panel principal</div>
            <h1 className="lx-section-title mt-1">Dashboard</h1>
            <p className="text-lx-muted text-sm mt-1">Resumen operativo — {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</p>
          </div>
        </div>
      )}

      {/* Period selector */}
      <div className="space-y-3" data-testid="period-selector">
        <div className="flex flex-wrap items-center gap-2">
          <span className="lx-overline mr-1">Período:</span>
          {PERIODS.map((p) => (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              data-testid={`period-${p.v}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${period === p.v ? "bg-lx-primary text-white border-lx-primary" : "bg-white text-lx-muted border-lx-border hover:border-lx-primary/40"}`}
            >
              {p.l}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-lx-surface border border-lx-border" data-testid="custom-period-pickers">
            <div>
              <label className="lx-label">Desde</label>
              <input type="date" className="lx-input" value={customStart} onChange={(e)=>setCustomStart(e.target.value)} data-testid="custom-period-start"/>
            </div>
            <div>
              <label className="lx-label">Hasta</label>
              <input type="date" className="lx-input" value={customEnd} onChange={(e)=>setCustomEnd(e.target.value)} data-testid="custom-period-end"/>
            </div>
            {(!customStart || !customEnd) && (
              <span className="text-xs text-lx-muted pb-2.5">Selecciona ambas fechas para ver el reporte.</span>
            )}
          </div>
        )}
      </div>

      {/* Main row: compliance gauge + stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lx-card flex flex-col items-center justify-center text-center" data-testid="overall-compliance-card">
          <ComplianceGauge value={compliance?.overall?.percentage} label="Cumplimiento" size={160}/>
          <div className="mt-3 text-xs text-lx-muted">
            {compliance?.overall?.actual ?? 0} de {compliance?.overall?.expected ?? 0} checks · {periodLabel.toLowerCase()}
          </div>
          {!isClient && (
            <Link to="/compliance" className="text-xs font-semibold text-lx-primary mt-2 inline-flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="view-compliance-detail">
              Ver detalle <ArrowRight size={12}/>
            </Link>
          )}
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <StatCard testid="stat-checks-today" icon={Activity} label="Checks hoy" value={stats.total_checks_today} />
          <StatCard testid="stat-areas-cleaned" icon={CheckCircle2} label="Áreas limpiadas hoy" value={stats.areas_cleaned_today} color="#10B981" />
          <StatCard testid="stat-areas-pending" icon={Clock} label="Áreas pendientes" value={stats.areas_pending} color="#F59E0B" />
          <StatCard testid="stat-issues" icon={AlertCircle} label="Incidencias hoy" value={stats.issues_today} color="#EF4444" />
          {!isClient && <StatCard testid="stat-clients" icon={Building2} label="Clientes" value={stats.total_clients} color="#475569" />}
          <StatCard testid="stat-locations" icon={MapPin} label={isClient ? "Mis sucursales" : "Sucursales"} value={stats.total_locations} color="#475569" />
          <StatCard testid="stat-areas" icon={QrCode} label={isClient ? "Mis áreas" : "Áreas totales"} value={stats.total_areas} color="#475569" />
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar chart - compliance per scope */}
        <div className="lx-card lg:col-span-2 relative" data-testid="compliance-bar-chart">
          {complianceLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10" data-testid="compliance-loading-overlay">
              <div className="flex items-center gap-2 text-lx-primary text-sm font-semibold">
                <div className="w-4 h-4 border-2 border-lx-primary border-t-transparent rounded-full animate-spin"></div>
                Actualizando…
              </div>
            </div>
          )}
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-bold text-lg">
                Cumplimiento por {complianceScope === "client" ? "cliente" : complianceScope === "area" ? "área" : "sucursal"}
              </h3>
              <p className="text-xs text-lx-muted">{periodLabel} · top 8</p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              {!isClient && (
                <div>
                  <label className="lx-label text-[10px]">Agrupar por</label>
                  <select
                    className="lx-input py-1.5 text-sm"
                    value={complianceScope}
                    onChange={(e) => setComplianceScope(e.target.value)}
                    data-testid="compliance-scope-select"
                  >
                    <option value="client">Cliente</option>
                    <option value="location">Sucursal</option>
                    <option value="area">Área</option>
                  </select>
                </div>
              )}
              {!isClient && complianceScope !== "client" && (
                <div>
                  <label className="lx-label text-[10px]">Cliente</label>
                  <select
                    className="lx-input py-1.5 text-sm"
                    value={complianceClientFilter}
                    onChange={(e) => setComplianceClientFilter(e.target.value)}
                    data-testid="compliance-client-filter"
                  >
                    <option value="">Todos</option>
                    {allClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
          {topLocations.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-lx-muted text-sm">
              {complianceScope === "client" ? "Sin clientes con áreas configuradas." :
               complianceScope === "area" ? "Sin áreas configuradas." :
               "Sin sucursales activas con áreas configuradas."}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(260, topLocations.length * 38)}>
              <BarChart data={topLocations} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false}/>
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748B" }} unit="%"/>
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#0F172A" }} width={130}/>
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
                  formatter={(val, _, props) => [`${val}% (${props.payload.actual}/${props.payload.expected})`, "Cumplimiento"]}
                />
                <Bar dataKey="percentage" radius={[0, 6, 6, 0]}>
                  {topLocations.map((e, i) => <Cell key={i} fill={ComplianceColor(e.percentage)}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie chart - distribution by status */}
        <div className="lx-card" data-testid="distribution-pie-chart">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-lg">Distribución de estatus</h3>
            <p className="text-xs text-lx-muted">{periodLabel}</p>
          </div>
          {distData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-lx-muted text-sm">Sin registros en el período.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={distData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {distData.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.key] || "#94A3B8"}/>)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Trend chart - last 30 days */}
      <div className="lx-card" data-testid="trends-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-lx-primary"/>
            <h3 className="font-heading font-bold text-lg">Tendencia de check-ins · últimos 30 días</h3>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={trendData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} interval={Math.max(0, Math.floor(trendData.length / 10) - 1)}/>
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false}/>
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}/>
            <Line type="monotone" dataKey="checks" stroke="#00AEEF" strokeWidth={2.5} dot={{ r: 3, fill: "#00AEEF" }} activeDot={{ r: 5 }}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Recent activity */}
      <div className="lx-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Últimos registros</h3>
          <span className="text-xs text-lx-muted">{stats.recent_checkins?.length || 0} más recientes</span>
        </div>
        {stats.recent_checkins?.length ? (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead>
                <tr><th>Fecha</th><th>Área</th><th>Sucursal</th><th>Usuario</th><th>Estatus</th></tr>
              </thead>
              <tbody>
                {stats.recent_checkins.map((c, i) => (
                  <tr key={c.id || i} data-testid={`recent-row-${i}`}>
                    <td className="text-lx-muted">{fmtDate(c.created_at)}</td>
                    <td className="font-medium">{c.area_name || "—"}</td>
                    <td>{c.location_name || "—"}</td>
                    <td>{c.user_name || "—"}</td>
                    <td>{statusBadge(c.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 text-lx-muted text-sm">Aún no hay registros.</div>
        )}
      </div>
    </div>
  );
}
